import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import type { DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

const TELEGRAM_ICON = (
  <svg className="h-8 w-8 text-[#26A5E4]" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
)

const TELEGRAM_ICON_MUTED = (
  <svg className="h-8 w-8 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
)

interface Props {
  worker: DigitalWorkerDetail
  canManage: boolean
}

export default function TelegramChannelCard({ worker, canManage }: Props) {
  const [modalOpen, setModalOpen] = useState(false)
  const [botToken, setBotToken] = useState('')
  const [connecting, setConnecting] = useState(false)

  const isConfigured = !!worker.telegram_bot_username
  const status = worker.telegram_connection_status
  const isReady = worker.provisioning_status === 'ready'
  const canConnect = canManage && isReady && worker.runtime_capabilities.telegram.supported
  // Semânticas dos tokens (ok/warn/err em soft) — sem paleta crua.
  const statusCopy = {
    active: {
      label: 'Ativo',
      classes: 'bg-success/12 text-success',
      dot: 'bg-success',
      panel: 'border-success/40 bg-success/10',
    },
    pending: {
      label: 'Ativando',
      classes: 'bg-warning/13 text-warning',
      dot: 'bg-warning',
      panel: 'border-warning/40 bg-warning/10',
    },
    failed: {
      label: 'Falhou',
      classes: 'bg-destructive/12 text-destructive',
      dot: 'bg-destructive',
      panel: 'border-destructive/40 bg-destructive/10',
    },
    inactive: {
      label: 'Inativo',
      classes: 'bg-muted text-muted-foreground',
      dot: 'bg-muted-foreground',
      panel: 'border-border bg-card',
    },
  }[status]

  if (isConfigured) {
    return (
      <div className={`relative rounded-xl border p-4 ${statusCopy.panel}`}>
        <div className="flex items-center gap-3">
          {TELEGRAM_ICON}
          <div>
            <p className="font-medium text-foreground">Telegram</p>
            <p className="text-xs text-muted-foreground">@{worker.telegram_bot_username}</p>
          </div>
        </div>
        <div className="absolute top-2 right-2">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${statusCopy.classes}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusCopy.dot}`} />
            {worker.runtime_capabilities.telegram.supported ? statusCopy.label : "Indisponível"}
          </span>
        </div>
        {canManage && (
          <button
            type="button"
            className="mt-3 text-xs text-destructive transition-opacity duration-150 hover:opacity-80 cursor-pointer"
            onClick={() => {
              if (confirm('Desconectar o Telegram deste colaborador?')) {
                router.delete(`/dashboard/digital_workers/${worker.id}/disconnect_telegram`, { preserveScroll: true })
              }
            }}
          >
            Desconectar
          </button>
        )}
      </div>
    )
  }

  if (canConnect) {
    return (
      <>
        <button
          type="button"
          className="rounded-lg border border-border bg-card p-4 hover:border-[#26A5E4] hover:bg-[#26A5E4]/5 transition-colors cursor-pointer text-left w-full"
          onClick={() => setModalOpen(true)}
        >
          <div className="flex items-center gap-3">
            {TELEGRAM_ICON}
            <div>
              <p className="font-medium text-foreground">Telegram</p>
              <p className="text-xs text-[#26A5E4]">Conectar</p>
            </div>
          </div>
        </button>
        <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) { setModalOpen(false); setBotToken('') } }}>
          <DialogContent className="max-w-[400px]">
            <DialogHeader><DialogTitle>Conectar Telegram</DialogTitle></DialogHeader>
            <div className="grid gap-2">
              <Label>Token do Bot</Label>
              <Input type="password" value={botToken} onChange={(e) => setBotToken(e.target.value)} />
              <p className="text-sm text-muted-foreground">Obtenha o token com @BotFather no Telegram</p>
            </div>
            <DialogFooter>
              <Button
                disabled={connecting || !botToken.trim()}
                onClick={() => {
                  setConnecting(true)
                  router.post(`/dashboard/digital_workers/${worker.id}/connect_telegram`, { bot_token: botToken }, {
                    preserveScroll: true,
                    onSuccess: () => { setModalOpen(false); setBotToken('') },
                    onFinish: () => setConnecting(false),
                  })
                }}
              >
                {connecting && <Loader2 className="h-4 w-4 animate-spin" />}
                Conectar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-muted/50 p-4 opacity-60 cursor-not-allowed">
      <div className="flex items-center gap-3">
        {TELEGRAM_ICON_MUTED}
        <div>
          <p className="font-medium text-foreground">Telegram</p>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {!isReady ? 'Aguardando provisionamento' : 'Indisponível'}
          </span>
        </div>
      </div>
    </div>
  )
}
